/* CNC Slide Cart V3
   cnc-atc-to-drawer.js owns the server requests and the full section refresh.
   Dawn's theme.js owns scroll and animations.
   V3 owns: optimistic UI, and sending the customer to checkout.

   Optimistic UI: instantly updates prices, total and icons on click, so the
   number the customer reads is correct before any server round trip lands.

   Removed 2026-09-27: the header/footer re-fetch, superseded by
   cnc-atc-to-drawer's whole-section replace.

   Inert as of the same date: the suggestion-checkbox handler below. Its markup
   is out of quick-cart.liquid and nothing else emits data-cnc-suggestion-add, so
   it can never fire. Left in place rather than deleted because the upsell removal
   is a judgement on thin evidence (n=5) and may be reverted; the handler is the
   cheap half to keep. Its CSS in cnc-slide-cart-v3.css is unused for the same
   reason. If the upsell stays gone, both are safe to drop. */

(function() {
  'use strict';

  /* ============================================================
     MONEY FORMAT
     ============================================================ */

  function formatMoney(cents) {
    var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    return fmt.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
  }

  /* ============================================================
     HEADER + FOOTER REFRESH — REMOVED 2026-09-27 (Tracy: "anything too slow to
     click/load/change is the biggest distraction")

     WHAT IT COST, measured on the live AU store rather than estimated:
       POST /cart/add.js                     384ms cold / 457ms warm
       GET  /cart?section_id=quick-cart       662ms cold / 330ms warm
     This function added a SECOND fetch of that same section, behind a hard-coded
     600ms debounce — so roughly 950ms of the ~2s add-to-bag, every single time.
     It is also why the drawer appeared to change under you after it had settled:
     the late response rewrote the heading and footer once you were already
     reading them.

     WHY IT IS SAFE TO DELETE RATHER THAN TUNE. The header above described the
     world it was written for — "Theme.js refreshes .quick-cart__items but NOT the
     heading or footer" — and that world is gone. cnc-atc-to-drawer.js now owns
     the refresh and its refreshCartSection() innerHTML-replaces the WHOLE
     [data-section-type="quick-cart"] element. Both targets live inside it
     (.quick-cart__heading and .quick-cart__footer-inner are nested within that
     same section in quick-cart.liquid), so they were already being replaced with
     fresh server HTML a beat before this ran.

     And it ran on BOTH paths, not just add: refreshCartSection() is called on the
     add path and again on the qty-change reconcile, so there was no case left
     where this was the only thing keeping the heading honest.

     Checked before deleting, because "nothing dispatches it" is the kind of claim
     that is wrong: custom-events.js intercepts window.fetch and emits
     'cart:updated' on any cart call, so BOTH listeners fired on an ordinary add.
     Removing only one would have changed nothing.

     The optimistic UI below already updates the total and the heading count on
     click, so nothing here was providing the number the customer actually sees —
     only a slower confirmation of it.
     ============================================================ */

  /* ============================================================
     OPTIMISTIC UI
     Instantly updates line prices, checkout total, heading count,
     and trash/minus icons when qty buttons or remove are clicked.
     Does NOT modify the qty input value — theme.js reads that to
     calculate the server request. Prices update first, qty number
     follows when theme's section refresh lands.
     ============================================================ */

  var TRASH_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';
  var MINUS_SVG = '<span class="icon icon-new icon-minus-small"><svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 10.75H.25v2.5H1.5v-2.5zm21 2.5h1.25v-2.5H22.5v2.5zm-21 0H12v-2.5H1.5v2.5zm10.5 0h10.5v-2.5H12v2.5z" fill="currentColor"/></svg></span>';

  document.addEventListener('click', function(e) {
    var addBtn = e.target.closest('[data-add-quantity]');
    var subBtn = e.target.closest('[data-subtract-quantity]');
    var removeBtn = e.target.closest('[data-remove-item]');

    if (!addBtn && !subBtn && !removeBtn) return;

    var item = (addBtn || subBtn || removeBtn).closest('[data-input-item]');
    if (!item || item.hasAttribute('data-cnc-gwp')) return;

    var input = item.querySelector('[data-quantity-input]');
    var unitPrice = parseInt(item.getAttribute('data-unit-price') || '0', 10);
    var currentQty = input ? parseInt(input.value, 10) : 1;
    var btn = document.querySelector('[data-checkout-btn]');
    var currentTotal = btn ? parseInt(btn.getAttribute('data-checkout-total') || '0', 10) : 0;

    if (removeBtn || (subBtn && currentQty <= 1)) {
      /* ── REMOVE ── */
      var linePrice = unitPrice * currentQty;

      /* Animate out */
      item.style.transition = 'opacity 200ms ease';
      item.style.opacity = '0.15';

      /* Update total */
      var newTotal = Math.max(0, currentTotal - linePrice);
      updateButton(btn, newTotal, currentQty);

      /* Update heading count */
      updateHeadingCount(-currentQty);

    } else if (addBtn) {
      /* ── QTY UP ── */
      var newQty = currentQty + 1;

      /* Update line price */
      updateLinePrice(item, unitPrice, newQty);

      /* Update total */
      updateButton(btn, currentTotal + unitPrice, 1);

      /* Update heading count */
      updateHeadingCount(1);

      /* Swap icon if going from 1 to 2 */
      if (currentQty === 1) {
        var minusBtn = item.querySelector('[data-subtract-quantity]');
        if (minusBtn) minusBtn.innerHTML = MINUS_SVG;
      }

    } else if (subBtn && currentQty > 1) {
      /* ── QTY DOWN ── */
      var newQty = currentQty - 1;

      /* Update line price */
      updateLinePrice(item, unitPrice, newQty);

      /* Update total */
      updateButton(btn, currentTotal - unitPrice, -1);

      /* Update heading count */
      updateHeadingCount(-1);

      /* Swap icon if going to 1 — after this click has finished. Swapping it here
         replaced the very icon the shopper tapped, so assets/cnc-atc-to-drawer.js (which
         handles the same click after this listener) found the tap target detached, could
         not find its button, and never sent the change: 2 -> 1 showed the lower total but
         the bag stayed at 2 (found 2026-10-04, live and staging). */
      if (newQty === 1) {
        var minusBtn = item.querySelector('[data-subtract-quantity]');
        if (minusBtn) setTimeout(function () { minusBtn.innerHTML = TRASH_SVG; }, 0);
      }
    }
  });

  function updateLinePrice(item, unitPrice, newQty) {
    var priceEl = item.querySelector('.cnc-item__price');
    if (!priceEl) return;
    var newLine = unitPrice * newQty;
    priceEl.setAttribute('data-line-price', newLine);
    /* The visible number is the UNIT price (sections/quick-cart.liquid renders item_price,
       not the line total), so it does not change with quantity. Writing unit x qty here
       made it jump to the line total on + and snap back when the drawer re-rendered
       (Tracy 2026-10-04: "that number's trying to change and then it reverts back").
       The total lives on the checkout button. */
  }

  function updateButton(btn, newTotal, qtyDelta) {
    if (!btn) return;
    btn.setAttribute('data-checkout-total', newTotal);
    /* Parse currency code from existing text: "$45.98 AUD — CHECKOUT" */
    var text = btn.textContent;
    var dashIdx = text.indexOf('\u2014');
    if (dashIdx === -1) dashIdx = text.indexOf('—');
    if (dashIdx === -1) dashIdx = text.indexOf('-');
    var suffix = dashIdx > -1 ? text.substring(dashIdx) : '— CHECKOUT';
    /* Extract currency between price and dash */
    var parts = text.substring(0, dashIdx).trim().split(/\s+/);
    var currency = parts.length > 1 ? parts[parts.length - 1] : '';
    btn.textContent = formatMoney(newTotal) + ' ' + currency + ' ' + suffix;
  }

  function updateHeadingCount(delta) {
    var heading = document.querySelector('.quick-cart__heading');
    if (!heading) return;
    var text = heading.textContent;
    var match = text.match(/\((\d+)\)/);
    if (match) {
      var newCount = Math.max(0, parseInt(match[1], 10) + delta);
      heading.textContent = text.replace(/\(\d+\)/, '(' + newCount + ')');
    }
  }

  /* ============================================================
     SUGGESTION CHECKBOX
     ============================================================ */

  var suggestionBusy = false;

  document.addEventListener('change', function(e) {
    var checkbox = e.target.closest('[data-cnc-suggestion-add]');
    if (!checkbox || !checkbox.checked || suggestionBusy) return;

    var variantId = parseInt(checkbox.getAttribute('data-cnc-suggestion-add'), 10);
    var block = checkbox.closest('[data-cnc-suggestion]');
    if (!variantId) return;

    suggestionBusy = true;

    if (block) {
      block.style.transition = 'opacity 300ms ease';
      block.style.opacity = '0.4';
      block.style.pointerEvents = 'none';
    }

    Promise.all([
      fetch(window.cncUrl('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      }),
      fetch(window.cncUrl('cart/update.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { cnc_suggestion_used: 'true' } })
      })
    ])
    .then(function() {
      if (block) block.style.opacity = '0';
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
      suggestionBusy = false;
    })
    .catch(function() {
      checkbox.checked = false;
      if (block) {
        block.style.opacity = '1';
        block.style.pointerEvents = '';
      }
      suggestionBusy = false;
    });
  });

  /* ============================================================
     PERSONALITY TOAST — REMOVED 2026-09-27 (Tracy)

     The markup is gone from quick-cart.liquid, and the two MutationObservers
     that drove it are gone with it. Leaving them would have been invisible but
     not free: one watched the cart section with { childList: true, subtree: true }
     and re-armed a 250ms timer on every mutation, so every optimistic price
     update re-triggered a callback that could only ever find nothing.

     Removing the driver at the same time as the markup, rather than relying on
     showPersonalityToast()'s `if (!el) return;` to swallow it quietly.
     ============================================================ */

  /* ============================================================
     CHECKOUT — GO STRAIGHT THERE

     Tracy measured ~3 seconds from tapping CHECKOUT to seeing checkout.
     The button is <button type="submit" name="checkout"> inside the cart form,
     so a tap was POST /cart -> 302 -> load checkout: three round trips before
     anything painted. Nothing in this file was slowing it; the shape was.

     The cart is already persisted server-side — every qty change posts on its
     own — so the form submit was only carrying attributes[gift]. That now saves
     on CHANGE (below), while they are still in the drawer, which leaves the
     button with nothing to submit and lets it simply navigate.

     type="submit" is deliberately LEFT on the button: if this script fails to
     load, the browser still posts the form and checkout still works. This is an
     enhancement over a working path, never a replacement for one.

     window.cncUrl() and not '/checkout': Shopify.routes.root carries the market
     prefix, and a hardcoded path would drop a non-primary market's customer out
     of their locale on the most important click in the session.
     ============================================================ */

  /* ⛔ REVERTED 2026-09-28. DO NOT REINSTATE WITHOUT READING THIS.

     Tracy, on live, the morning after this shipped: "when I go from cart to
     checkout, I'm only getting shop pay. I'm not getting the normal checkout page."

     POST /cart (name=checkout) and GET /checkout are NOT the same route, and I
     treated them as if they were. The form POST goes through Shopify's own cart
     handler, which decides where the buyer belongs. A direct GET to /checkout is
     a route Shopify may resolve straight to Shop Pay for a buyer it recognises —
     so the accelerated flow stopped being a CHOICE the customer makes and became
     the only door. That is a worse checkout, and it is on the one path where
     being wrong costs an order rather than a second.

     The speed argument was real — the POST is three round trips — but the whole
     point of shaving it was to remove friction, and this added a different
     friction while hiding the standard checkout. Correctness on the money path
     outranks the ~1s. The button goes back to being a plain form submit, which is
     what type="submit" name="checkout" in quick-cart.liquid has always done.

     The gift-attribute AJAX below STAYS. It is independent of this, it works, and
     it means the form has less to carry if a safe fast path is found later. If
     one is attempted again it must be tested against a browser with a live Shop
     Pay session — which is exactly the case that was never exercised here, and
     the reason this reached Tracy instead of a test. */

  /* (checkout click interceptor removed — the button submits the cart form) */

  /* Gift attribute, saved the moment it is ticked rather than at submit time.
     Fire-and-forget is acceptable here and nowhere else: the value is cosmetic,
     the customer is still in the drawer with seconds to spare before they click
     checkout, and a failure costs a gift note rather than an order. */
  document.addEventListener('change', function(e) {
    var gift = e.target.closest('[name="attributes[gift]"]');
    if (!gift) return;

    fetch(window.cncUrl('cart/update.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { gift: gift.checked ? 'true' : '' } })
    }).catch(function() { /* cosmetic attribute — never block the cart on it */ });
  });

  /* GWP: handled by Easy Gift app. */
})();
